import "@/App.css";
import { SpeedInsights } from '@vercel/speed-insights/react';
import React, { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ConfigProvider } from "@/contexts/ConfigContext";
import ThemeLanguageToggle from "@/components/ThemeLanguageToggle";
import GlobalHelpButton from "@/components/GlobalHelpButton";
import Loader from "@/components/ui/Loader";
import { BackgroundBeams } from "@/components/ui/background-beams";
import ErrorBoundary from "@/components/ErrorBoundary";

// Only entry-point pages loaded eagerly (Login + Landing)
import LoginPage from "@/pages/LoginPage";
import Landing from "@/pages/Landing";
import SubscriptionGuard from "@/components/Auth/SubscriptionGuard";

// ALL other pages lazy-loaded (code splitting)
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ClientsPage = lazy(() => import("@/pages/ClientsPage"));
const BulkUploadPage = lazy(() => import("@/pages/BulkUploadPage"));
const SubscriptionPlans = lazy(() => import("@/pages/SubscriptionPlans"));

// Agency Pages
const AgencyDashboard = lazy(() => import("@/pages/agency/AgencyDashboard"));
const AgencyProfile = lazy(() => import("@/pages/agency/AgencyProfile"));
const AgencyConsolidation = lazy(() => import("@/pages/agency/AgencyConsolidation"));
const AgencyAgents = lazy(() => import("@/pages/agency/AgencyAgents"));
const AgencyRules = lazy(() => import("@/pages/agency/AgencyRules"));
const AgencyMappings = lazy(() => import("@/pages/agency/AgencyMappings"));
const AgencyMessages = lazy(() => import("@/pages/agency/AgencyMessages"));
const AgencyActiveClients = lazy(() => import("@/pages/agency/AgencyActiveClients"));
const AgencyCanceledClients = lazy(() => import("@/pages/agency/AgencyCanceledClients"));
const AgencyClients = lazy(() => import("@/pages/agency/AgencyClients"));
const AgencyAgentPerformance = lazy(() => import("@/pages/agency/AgencyAgentPerformance"));
const AgencyDailyActivity = lazy(() => import("@/pages/agency/AgencyDailyActivity"));


// Agent Pages
const AgentDashboard = lazy(() => import("@/pages/agent/AgentDashboard"));
const AgentClients = lazy(() => import("@/pages/agent/AgentClients"));
const AgentActiveClients = lazy(() => import("@/pages/agent/AgentActiveClients"));
const AgentCancelledClients = lazy(() => import("@/pages/agent/AgentCancelledClients"));
const AgentReconciliation = lazy(() => import("@/pages/agent/AgentReconciliation"));
const AgentProfile = lazy(() => import("@/pages/agent/AgentProfile"));
const AgentAnalytics = lazy(() => import("@/pages/agent/AgentAnalytics"));
const AgentBookUpload = lazy(() => import("@/pages/agent/AgentBookUpload"));
const AgentAgency = lazy(() => import("@/pages/agent/AgentAgency"));
const AgentMessages = lazy(() => import("@/pages/agent/AgentMessages"));
const AgentTasks = lazy(() => import("@/pages/agent/AgentTasks"));

// Analytics
const AnalyticsDashboard = lazy(() => import("@/pages/analytics/AnalyticsDashboard"));

// FMO Pages
const FMODashboard = lazy(() => import("@/pages/fmo/FMODashboard"));
const FMOOverrides = lazy(() => import("@/pages/fmo/FMOOverrides"));
const FMOOverrideRules = lazy(() => import("@/pages/fmo/FMOOverrideRules"));
const FMOHierarchy = lazy(() => import("@/pages/fmo/FMOHierarchy"));
const FMOSubAgencies = lazy(() => import("@/pages/fmo/FMOSubAgencies"));
const FMOAgents = lazy(() => import("@/pages/fmo/FMOAgents"));
const FMOReconciliation = lazy(() => import("@/pages/fmo/FMOReconciliation"));
const FMOConsolidation = lazy(() => import("@/pages/fmo/FMOConsolidation"));
const FMOSettings = lazy(() => import("@/pages/fmo/FMOSettings"));



// Protected Route Component (Solo verifica autenticación y roles)
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { isAuthenticated, loading, profile, user, isAuthReady, logout } = useAuth();
  const [profileTimeout, setProfileTimeout] = useState(false);

  // Safety timeout: if auth is done and user is present but profile is null for 10s, show recovery UI
  useEffect(() => {
    if (!loading && isAuthReady && user && !profile) {
      const timer = setTimeout(() => setProfileTimeout(true), 10000);
      return () => clearTimeout(timer);
    }
    setProfileTimeout(false);
  }, [loading, isAuthReady, user, profile]);

  if (loading || !isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agency"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Profile hasn't loaded yet (still fetching) — show spinner or recovery UI if timeout fired
  if (!profile) {
    if (profileTimeout) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 text-center p-4">
          <h2 className="text-xl font-semibold text-foreground">Error al cargar la sesión</h2>
          <p className="text-muted-foreground text-sm max-w-sm">No pudimos obtener los datos de tu perfil. Esto suele deberse a una sesión desincronizada.</p>
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
            >
              Reintentar
            </button>
            <button
              onClick={() => logout()}
              className="px-4 py-2 bg-slate-800 text-slate-200 rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium"
            >
              Cerrar Sesión (Reconectar)
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agency"></div>
      </div>
    );
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(profile.role)) {
    if (profile.role === 'agent') {
      return <Navigate to="/agent/dashboard" replace />;
    }
    if (profile.role === 'fmo_admin') {
      return <Navigate to="/fmo/dashboard" replace />;
    }
    if (profile.role === 'dhm_supervisor' || profile.role === 'agency_admin') {
      return <Navigate to="/agency/dashboard" replace />;
    }
    // Role exists but doesn't match any known role — show neutral message,
    // never redirect to /login (that causes the loop).
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center p-4">
        <h1 className="text-2xl font-bold mb-2">Acceso no permitido</h1>
        <p className="text-muted-foreground">Tu rol no tiene acceso a esta sección.</p>
      </div>
    );
  }

  return children;
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, loading, profile, isAuthReady } = useAuth();

  if (loading || !isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agency"></div>
      </div>
    );
  }

  if (isAuthenticated && profile) {
    if (profile.role === 'agent') {
      return <Navigate to="/agent/dashboard" replace />;
    }
    if (profile.role === 'fmo_admin') {
      return <Navigate to="/fmo/dashboard" replace />;
    }
    if (profile.role === 'dhm_supervisor' || profile.role === 'agency_admin') {
      return <Navigate to="/agency/dashboard" replace />;
    }
    // If we don't recognize the role or they don't have one, logout or show error.
    // For now, we will clear their auth or just redirect to a neutral page. But wait, if they have no role, let's just let them stay on the page or redirect to an error route. 
    // Wait, if they are authenticated but have no valid role, they can't access ANY dashboard.
    // We should probably log them out or show a message.
    // Let's redirect them to an error page or just /subscription/plans if that's safe, but /subscription/plans requires allowedRoles.
    // We'll return them to a new route or just render an error.
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center p-4">
        <h1 className="text-2xl font-bold mb-2">Error de Acceso</h1>
        <p className="text-muted-foreground mb-4">Tu cuenta no tiene un rol válido asignado. Por favor contacta a soporte.</p>
      </div>
    );
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Rutas Públicas */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />

      {/* Rutas Privadas (Solo requieren Login) */}
      <Route element={<ProtectedRouteWrapper />}>
        {/* La página de pago debe ser accesible sin pagar, pero requiere login */}
        <Route path="/subscription/plans" element={
          <ProtectedRoute allowedRoles={['agency_admin', 'agent', 'fmo_admin']}>
            <SubscriptionPlans />
          </ProtectedRoute>
        } />

        {/* Rutas Protegidas (Requieren Login + PAGO) */}
        <Route element={<SubscriptionGuard />}>
          {/* Agency Routes - Protected for agency_admin */}
          <Route path="/agency/dashboard" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyDashboard />
            </ProtectedRoute>
          } />
          <Route path="/agency/agents" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyAgents />
            </ProtectedRoute>
          } />
          <Route path="/agency/rules" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyRules />
            </ProtectedRoute>
          } />

          <Route path="/agency/consolidation" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyConsolidation />
            </ProtectedRoute>
          } />

          <Route path="/agency/mappings" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyMappings />
            </ProtectedRoute>
          } />

          <Route path="/agency/profile" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyProfile />
            </ProtectedRoute>
          } />
          <Route path="/agency/agent-performance" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyAgentPerformance />
            </ProtectedRoute>
          } />
          <Route path="/agency/daily-activity" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyDailyActivity />
            </ProtectedRoute>
          } />

          <Route path="/agency/clients" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyClients />
            </ProtectedRoute>
          } />
          <Route path="/agency/clients/active" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyActiveClients />
            </ProtectedRoute>
          } />
          <Route path="/agency/clients/canceled" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyCanceledClients />
            </ProtectedRoute>
          } />
          <Route path="/agency/messages" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'dhm_supervisor']}>
              <AgencyMessages />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'agent', 'dhm_supervisor']}>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } />

          {/* Agent Routes - Protected for agent */}
          <Route path="/agent/dashboard" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/agent/clients" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentClients />
            </ProtectedRoute>
          } />
          <Route path="/agent/active-clients" element={<Navigate to="/agent/clients" replace />} />
          <Route path="/agent/cancelled-clients" element={<Navigate to="/agent/clients" replace />} />
          <Route path="/agent/book-upload" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentBookUpload />
            </ProtectedRoute>
          } />

          <Route path="/agent/agency" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentAgency />
            </ProtectedRoute>
          } />
          <Route path="/agent/messages" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentMessages />
            </ProtectedRoute>
          } />
          <Route path="/agent/tasks" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentTasks />
            </ProtectedRoute>
          } />
          <Route path="/agent/profile" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentProfile />
            </ProtectedRoute>
          } />
          {/* /agent/analytics → now a widget inside Dashboard */}
          <Route path="/agent/analytics" element={<Navigate to="/agent/dashboard" replace />} />

          <Route path="/agent/reconciliation" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <AgentReconciliation />
            </ProtectedRoute>
          } />


        </Route>

        {/* FMO Routes - Outside SubscriptionGuard (FMO has no subscription requirement) */}
        <Route path="/fmo/dashboard" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMODashboard />
          </ProtectedRoute>
        } />
        <Route path="/fmo/consolidation" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOConsolidation />
          </ProtectedRoute>
        } />
        <Route path="/fmo/overrides" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOOverrides />
          </ProtectedRoute>
        } />
        <Route path="/fmo/override-rules" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOOverrideRules />
          </ProtectedRoute>
        } />
        <Route path="/fmo/hierarchy" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOHierarchy />
          </ProtectedRoute>
        } />
        <Route path="/fmo/sub-agencies" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOSubAgencies />
          </ProtectedRoute>
        } />
        <Route path="/fmo/agents" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOAgents />
          </ProtectedRoute>
        } />
        <Route path="/fmo/reconciliation" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOReconciliation />
          </ProtectedRoute>
        } />
        <Route path="/fmo/settings" element={
          <ProtectedRoute allowedRoles={['fmo_admin']}>
            <FMOSettings />
          </ProtectedRoute>
        } />

      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

// Wrapper para rutas que solo requieren autenticación (sin suscripción)
function ProtectedRouteWrapper() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <Loader size={60} />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

function App() {
  return (
    <ErrorBoundary>
      <ConfigProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="fixed inset-0 -z-50 pointer-events-none">
              <BackgroundBeams />
            </div>
            <Suspense fallback={
              <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
                <Loader size={60} />
              </div>
            }>
              <AppRoutes />
            </Suspense>
            <GlobalHelpButton />
            <ThemeLanguageToggle />
            <SpeedInsights />
          </BrowserRouter>
        </AuthProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
